import { api } from '@/lib/api'

import type {
	Activity,
	ActivityDTO,
	PlaybooksResponse,
	WorkflowActionStatus,
} from './types'

/**
 * Fetch activity logs
 * @param tenant - The tenant ID
 * @param page - The page number
 * @param status - The status of the activity
 * @param type - The type of the activity
 */
export const fetchActivities = async (
	tenant: string,
	page: number = 1,
	limit: number = 20,
	status?: string,
	type?: 'adhoc' | 'workflow',
	signal?: AbortSignal,
) => {
	const baseURL = `/api/tenants/${tenant}/soar/actions_activity?page=${page}&per_page=${limit}`

	const searchParams = new URLSearchParams()

	if (status) {
		searchParams.set('status', status)
	}
	if (type) {
		searchParams.set('type', type)
	}

	const { data } = await api.get<Array<Activity>>(
		`${baseURL}&${searchParams.toString()}`,
		{
			signal,
		},
	)

	return data
}

/**
 * Fetch playbook activity logs
 * @param tenant - The tenant ID
 * @param page - The page number
 * @param status - The status of the activity
 * @param type - The type of the activity
 */
export const fetchPlaybookActivities = async (
	tenant: string,
	page: number = 1,
	status?: WorkflowActionStatus,
) => {
	const limit = 15
	const baseURL = `/api/tenants/${tenant}/soar/playbook_activity?skip=${page * limit}&limit=${limit}`

	const searchParams = new URLSearchParams()

	if (status) {
		searchParams.set('status', status)
	}

	const { data } = await api.get<PlaybooksResponse>(
		`${baseURL}&${searchParams.toString()}`,
	)

	return data.playbooks
}

/**
 * Fetch action activity details
 * @param tenant - The tenant ID
 * @param identifier - The identifier of the activity
 */
export const fetchActivityDetails = async (
	tenant: string,
	identifier: string | null,
	signal?: AbortSignal,
): Promise<ActivityDTO> => {
	if (!identifier) throw new Error('Identifier is required')

	const { data } = await api.get<ActivityDTO>(
		`/api/tenants/${tenant}/soar/actions_activity/${identifier}`,
		{
			signal,
		},
	)

	return data
}

/**
 * Check the status of the action
 * @param tenant - The tenant ID
 * @param action_uuid - The UUID of the action to check
 */
export async function getActionStatus(
	tenant: string,
	action_uuid: string | null,
	signal?: AbortSignal,
): Promise<ActivityDTO> {
	if (!action_uuid) throw new Error('Action UUID is required')

	return new Promise((resolve, reject) => {
		try {
			return api
				.get<ActivityDTO>(
					`/api/tenants/${tenant}/soar/actions_activity/${action_uuid}`,
					{
						signal,
					},
				)
				.then(({ data }) => resolve(data))
		} catch (error) {
			reject(error)
		}
	})
}
